import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { getTopicBySlug, topicSeoFromConfig, TOPIC_SEO_LIST } from "../constants/topicSeo";

export default function TopicLandingPage() {
  const { topicSlug } = useParams();
  const navigate = useNavigate();
  const topic = getTopicBySlug(topicSlug);

  useSeo(
    topic
      ? topicSeoFromConfig(topic)
      : { title: "专题服务", path: `/topic/${topicSlug || ""}`, noindex: true }
  );

  if (!topic) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 text-gray-500">
          <p>专题页面不存在</p>
          <button
            onClick={() => navigate("/topic")}
            className="px-4 py-2 rounded-full bg-pink-500 text-white text-sm"
          >
            查看全部专题
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const related = TOPIC_SEO_LIST.filter((item) => item.slug !== topic.slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <article className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6 md:p-10 space-y-6">
          <header className="space-y-3">
            <p className="text-sm text-pink-500 font-medium">{topic.badge}</p>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">{topic.headline}</h1>
            <p className="text-gray-600 leading-relaxed">{topic.intro}</p>
          </header>

          <section className="grid md:grid-cols-3 gap-4">
            {topic.features.map((item) => (
              <div key={item.title} className="rounded-xl bg-rose-50 p-4">
                <h2 className="font-bold text-pink-600 mb-2">{item.title}</h2>
                <p className="text-sm text-gray-600">{item.text}</p>
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-bold text-gray-800">常见问题</h2>
            <div className="space-y-3 text-sm text-gray-600">
              {topic.faqs.map((item) => (
                <div key={item.q} className="rounded-xl border border-pink-100 p-4">
                  <h3 className="font-semibold text-gray-800 mb-1">{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="flex flex-wrap gap-3 pt-2">
            <Link
              to={topic.primaryCta.to}
              className="px-6 py-3 bg-pink-500 text-white rounded-full text-sm font-medium hover:bg-pink-600 transition"
            >
              {topic.primaryCta.label}
            </Link>
            <Link
              to={topic.secondaryCta.to}
              className="px-6 py-3 border border-pink-400 text-pink-600 rounded-full text-sm hover:bg-pink-50 transition"
            >
              {topic.secondaryCta.label}
            </Link>
            {topic.tertiaryCta && (
              <Link
                to={topic.tertiaryCta.to}
                className="px-6 py-3 border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-gray-50 transition"
              >
                {topic.tertiaryCta.label}
              </Link>
            )}
          </div>
        </article>

        <section className="max-w-3xl mx-auto mt-8">
          <h2 className="text-sm text-gray-500 mb-3">更多专题</h2>
          <div className="flex flex-wrap gap-2">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={`/topic/${item.slug}`}
                className="px-3 py-1.5 bg-white rounded-full text-sm text-gray-600 border border-pink-100 hover:border-pink-300 hover:text-pink-600 transition"
              >
                {item.badge}
              </Link>
            ))}
            <Link to="/topic" className="px-3 py-1.5 text-sm text-pink-500 hover:underline">
              全部专题 →
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
